import { Link } from "react-router-dom";
import { format } from "date-fns";
import Paper from "@mui/material/Paper";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
} from "@mui/material";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import { LuArrowUpDown } from "react-icons/lu";

import { pathToRobotDetail } from "../../../helpers/paths";
import useSort from "../../../hooks/useSort";
import { ScrollableTable } from "../../templates/ScrollableTable";
import { CleaningRobotItem } from "types/response/robot";
import { hasWarningConsumablePart } from "lib/robot";
import { TuyaDeviceStatus } from "utils/robot/deviceStatus";
import { getDisplayValue, PartsStateChip } from "./PartsStateChip";
import { isNotCharging } from "helpers/robotHelper";

type RobotListTableProps = {
  robots: CleaningRobotItem[];
};

export const RobotListTable = ({ robots }: RobotListTableProps) => {
  const {
    order,
    orderBy,
    createSortHandler,
    stableSort,
    getComparator,
    orderByGetters,
  } = useSort<CleaningRobotItem>("asc", [(c) => c.robotName]);

  return (
    <ScrollableTable component={Paper}>
      <Table sx={{ whiteSpace: "nowrap" }}>
        <TableHead>
          <TableRow>
            <TableCell>
              <TableSortLabel
                active={orderBy === "hasWarning"}
                direction={orderBy === "hasWarning" ? order : "asc"}
                hideSortIcon={true}
                onClick={createSortHandler("hasWarning", [
                  (r) => r.consumableParts.some(hasWarningConsumablePart),
                ])}
              >
                {orderBy === "hasWarning" ? undefined : (
                  <LuArrowUpDown style={{ opacity: 0.3 }} />
                )}
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={orderBy === "name"}
                direction={orderBy === "name" ? order : "asc"}
                hideSortIcon={true}
                onClick={createSortHandler("name", [(r) => r.robotName])}
              >
                ロボット
                {orderBy === "name" ? undefined : (
                  <LuArrowUpDown style={{ opacity: 0.3 }} />
                )}
              </TableSortLabel>
            </TableCell>
            <TableCell style={{ minWidth: 70 }}>
              <TableSortLabel
                active={orderBy === "batteryRatio"}
                direction={orderBy === "batteryRatio" ? order : "asc"}
                hideSortIcon={true}
                onClick={createSortHandler("batteryRatio", [
                  (r) => {
                    const state =
                      r.deviceStatuses.find(
                        (s) => s.type === TuyaDeviceStatus.State
                      )?.status ?? "-";
                    const isCharging =
                      state === "charging" || state === "fullcharge";
                    const batteryRatio =
                      r.deviceStatuses.find((s) => s.type === "batteryRatio")
                        ?.status ?? "-";
                    return isCharging ? `充電中 ${batteryRatio}%` : `―`;
                  },
                ])}
              >
                バッテリー
                {orderBy === "batteryRatio" ? undefined : (
                  <LuArrowUpDown style={{ opacity: 0.3 }} />
                )}
              </TableSortLabel>
            </TableCell>
            <TableCell style={{ minWidth: 110 }}>
              <TableSortLabel
                active={orderBy === `consumableParts.state`}
                direction={orderBy === `consumableParts.state` ? order : "asc"}
                hideSortIcon={true}
                onClick={createSortHandler(`consumableParts.state`, [
                  (r) => {
                    const value = getDisplayValue(r.consumableParts);
                    return value.label;
                  },
                ])}
              >
                消耗品状態
                {orderBy === `consumableParts.state` ? undefined : (
                  <LuArrowUpDown style={{ opacity: 0.3 }} />
                )}
              </TableSortLabel>
            </TableCell>
            <TableCell style={{ minWidth: 100 }}>
              <TableSortLabel
                active={orderBy === "lastSyncDate"}
                direction={orderBy === "lastSyncDate" ? order : "asc"}
                hideSortIcon={true}
                onClick={createSortHandler("lastSyncDate", [
                  (r) => format(r.syncedAt, "M/d H:mm"),
                ])}
              >
                同期日時
                {orderBy === "lastSyncDate" ? undefined : (
                  <LuArrowUpDown style={{ opacity: 0.3 }} />
                )}
              </TableSortLabel>
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {stableSort(robots, getComparator(order, orderByGetters)).map(
            (r: CleaningRobotItem) => {
              const hasWarning = r.consumableParts.some(
                hasWarningConsumablePart
              ) || isNotCharging(r);
              const batteryRatio =
                r.deviceStatuses.find((s) => s.type === "batteryRatio")
                  ?.status ?? "-";
              return (
                <TableRow key={r.robotId}>
                  <TableCell>
                    {hasWarning ? (
                      <ReportProblemIcon fontSize="small" color="error" />
                    ) : (
                      <ReportProblemIcon
                        fontSize="small"
                        sx={{ color: "transparent" }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Link to={pathToRobotDetail(r.robotId)}>{r.robotName}</Link>
                  </TableCell>
                  <TableCell>{`${batteryRatio}%`}</TableCell>
                  <TableCell>
                    <PartsStateChip parts={r.consumableParts} />
                  </TableCell>
                  <TableCell>{format(r.syncedAt, "M/d H:mm")}</TableCell>
                </TableRow>
              );
            }
          )}
        </TableBody>
      </Table>
    </ScrollableTable>
  );
};
